import React, { useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Music, ArrowRight, Shield, Euro, Calendar, BarChart3,
  FileText, CreditCard, ClipboardList, ShoppingBag, Home
} from 'lucide-react';
import { AuthContext } from '../App';

const DEMO_PROFILES = [
  {
    userId: 'u1',
    login: 'admin',
    password: 'admin',
    label: 'Coordinateur',
    description: 'Accès complet à toutes les fonctionnalités',
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
    icon: Shield,
    access: [
      { icon: Home, label: 'Tableau de bord' },
      { icon: ClipboardList, label: 'Inscriptions' },
      { icon: ShoppingBag, label: 'Produits' },
      { icon: Calendar, label: 'Séances' },
      { icon: FileText, label: 'Facturation' },
      { icon: CreditCard, label: 'Paiements' },
    ],
  },
  {
    userId: 'u2',
    login: 'tresoriere',
    password: 'tresoriere',
    label: 'Trésorière',
    description: 'Gestion de la comptabilité et des relances',
    color: '#EC4899',
    gradient: 'linear-gradient(135deg, #DB2777, #F472B6)',
    icon: Euro,
    access: [
      { icon: FileText, label: 'Facturation' },
      { icon: CreditCard, label: 'Paiements & Relances' },
    ],
  },
  {
    userId: 'u3',
    login: 'prof',
    password: 'prof',
    label: 'Professeur',
    description: 'Suivi des séances et présences',
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #059669, #34D399)',
    icon: Calendar,
    access: [
      { icon: Calendar, label: 'Séances' },
    ],
  },
  {
    userId: 'u5',
    login: 'president',
    password: 'president',
    label: 'Président',
    description: 'Vue d\'ensemble de l\'association',
    color: '#3B82F6',
    gradient: 'linear-gradient(135deg, #2563EB, #60A5FA)',
    icon: BarChart3,
    access: [
      { icon: Home, label: 'Tableau de bord' },
    ],
  },
];

const Login = () => {
  const { handleLogin } = useContext(AuthContext);
  const navigate = useNavigate();

  const loginAs = (profile) => {
    const success = handleLogin(profile.login, profile.password);
    if (success) navigate('/');
  };

  return (
    <>
      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          background: #f8f9fa;
        }
        .login-branding {
          flex: 1;
          background: linear-gradient(135deg, #6c5ce7 0%, #a855f7 50%, #7c3aed 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          position: relative;
          overflow: hidden;
          color: #fff;
        }
        .login-branding::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px);
          background-size: 30px 30px;
          animation: float 20s linear infinite;
        }
        @keyframes float {
          0% { transform: translate(0, 0); }
          100% { transform: translate(30px, 30px); }
        }
        .login-branding-content {
          position: relative;
          z-index: 1;
          text-align: center;
        }
        .login-logo-circle {
          width: 100px;
          height: 100px;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255,255,255,0.3);
        }
        .login-branding h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
        }
        .login-branding p {
          font-size: 1.1rem;
          opacity: 0.9;
          max-width: 300px;
          line-height: 1.6;
        }
        .branding-notes {
          display: flex;
          gap: 2rem;
          margin-top: 3rem;
          opacity: 0.5;
          font-size: 2rem;
        }
        .login-form-side {
          flex: 1.2;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }
        .login-form-container {
          width: 100%;
          max-width: 520px;
        }
        .login-form-container h2 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 0.25rem;
        }
        .login-form-container .subtitle {
          color: #6b7280;
          margin-bottom: 2rem;
          font-size: 0.95rem;
        }
        .demo-profiles {
          display: flex;
          flex-direction: column;
          gap: 0.875rem;
        }
        .demo-profile-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.25rem;
          background: #fff;
          border: 2px solid #e5e7eb;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
          width: 100%;
          font-family: inherit;
          font-size: inherit;
        }
        .demo-profile-card:hover {
          border-color: var(--profile-color, #7c3aed);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transform: translateY(-2px);
        }
        .demo-profile-card:active {
          transform: translateY(0);
        }
        .profile-icon-circle {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          flex-shrink: 0;
        }
        .profile-main {
          flex: 1;
          min-width: 0;
        }
        .profile-label {
          font-size: 1rem;
          font-weight: 700;
          color: #1a1a2e;
          margin: 0 0 0.125rem;
        }
        .profile-desc {
          font-size: 0.8125rem;
          color: #6b7280;
          margin: 0 0 0.5rem;
        }
        .profile-access {
          display: flex;
          flex-wrap: wrap;
          gap: 0.375rem;
        }
        .profile-access-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.5rem;
          border-radius: 9999px;
          font-size: 0.6875rem;
          font-weight: 500;
          background: #f3f4f6;
          color: #4b5563;
        }
        .profile-arrow {
          color: #d1d5db;
          transition: color 0.2s, transform 0.2s;
          flex-shrink: 0;
        }
        .demo-profile-card:hover .profile-arrow {
          color: var(--profile-color, #7c3aed);
          transform: translateX(3px);
        }
        .login-inscription-link {
          margin-top: 1.5rem;
          text-align: center;
          padding: 1rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 12px;
        }
        .login-inscription-link p {
          font-size: 0.9rem;
          color: #4b5563;
          margin-bottom: 0.5rem;
        }
        .login-inscription-link a {
          color: #16a34a;
          font-weight: 600;
          text-decoration: none;
          font-size: 0.9rem;
        }
        .login-inscription-link a:hover {
          text-decoration: underline;
        }
        .demo-notice {
          margin-top: 1.25rem;
          padding: 0.75rem 1rem;
          background: #f3f0ff;
          border: 1px solid #e0d4fc;
          border-radius: 10px;
          text-align: center;
          font-size: 0.8rem;
          color: #7c3aed;
        }
        @media (max-width: 900px) {
          .login-branding {
            display: none;
          }
          .login-form-side {
            padding: 1.5rem;
          }
        }
      `}</style>

      <div className="login-page">
        <div className="login-branding">
          <div className="login-branding-content">
            <div className="login-logo-circle">
              <Music size={48} strokeWidth={1.5} />
            </div>
            <h1>Harmonie</h1>
            <p>École de musique associative — Gestion des inscriptions, cours et facturation</p>
            <div className="branding-notes">
              <span>♪</span>
              <span>♫</span>
              <span>♩</span>
              <span>♬</span>
            </div>
          </div>
        </div>

        <div className="login-form-side">
          <div className="login-form-container">
            <h2>Démonstration</h2>
            <p className="subtitle">Choisissez un profil pour explorer l'application</p>

            <div className="demo-profiles">
              {DEMO_PROFILES.map((profile) => {
                const ProfileIcon = profile.icon;
                return (
                  <button
                    key={profile.login}
                    className="demo-profile-card"
                    style={{ '--profile-color': profile.color }}
                    onClick={() => loginAs(profile)}
                  >
                    <div className="profile-icon-circle" style={{ background: profile.gradient }}>
                      <ProfileIcon size={24} />
                    </div>
                    <div className="profile-main">
                      <p className="profile-label">{profile.label}</p>
                      <p className="profile-desc">{profile.description}</p>
                      <div className="profile-access">
                        {profile.access.map((a, i) => {
                          const AccessIcon = a.icon;
                          return (
                            <span key={i} className="profile-access-tag">
                              <AccessIcon size={10} /> {a.label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <ArrowRight size={20} className="profile-arrow" />
                  </button>
                );
              })}
            </div>

            <br/>
            <h2>Inscription</h2>
            <p className="subtitle">ou consulter le formulaire d'inscription</p>

            <div className="login-inscription-link">
              <p> </p>
              <Link to="/inscription">
                Démonstration de l'inscription →
              </Link>
              <p> </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
